/* Derived views shared by several mock modules (payroll view, attendance, approvals, …).
 * Every number comes from the pure domain functions in src/domain. */
import { nitaqat, subAmount, paymentRef } from "../../domain/compliance";
import { addDays, daysTo, isISODate, monthEnd } from "../../domain/dates";
import { annualEntitlement, defaultAdj, mudadIssues, payrollTotals, payslip, usedAnnualDemo } from "../../domain/payroll";
import { processDay, summarize } from "../../domain/biometric";
import type * as T from "../types";
import { countryOf } from "../../domain/country";
import { E, fmtNum, sanitizeName, uuid, type Ctx } from "./ctx";

export function effAdj(c: Ctx, e: T.Employee): T.Adjustment {
  return c.state.run.adjustments[String(e.id)] ?? defaultAdj(e);
}

export function loansSorted(c: Ctx): T.Loan[] { return c.state.loans.slice().sort((a, b) => a.id - b.id); }

export function runView(c: Ctx): T.PayrollRunView {
  const run = c.state.run;
  const act = c.active();
  const loans = loansSorted(c);
  const slips = act.map((e) => payslip(e, effAdj(c, e), loans, c.state.settings.gosi, c.today, run.period, c.country));
  return { run, slips, totals: payrollTotals(slips), art92: slips.filter((s) => s.cap.over).map((s) => s.employeeId), mudad: mudadIssues(act) };
}

export function deviceNames(c: Ctx): Record<string, string> {
  const m: Record<string, string> = {};
  for (const d of c.state.devices) if (d.tenantId === c.tid) m[d.id] = d.name;
  return m;
}

const approvedLeaves = (c: Ctx) => c.state.leaves.filter((l) => l.status === "Approved");
const matchedPunches = (c: Ctx, from: string, toExcl: string) =>
  c.state.punches.filter((p) => p.employeeId != null && p.time >= from && p.time < toExcl);

export function dayView(c: Ctx, date: string): T.AttendanceDay {
  const rows = processDay({
    date, employeeIds: c.active().map((e) => e.id), punches: matchedPunches(c, date, addDays(date, 1)),
    leaves: approvedLeaves(c), policy: c.state.settings.attendance, nowHm: date === c.today ? c.nowHm : undefined, deviceNames: deviceNames(c),
  });
  return { date, rows, summary: summarize(rows) };
}

/** Processed attendance for each day of `period` up to today. */
export function periodRows(c: Ctx, period: string, empIds: number[]): T.DailyAttendance[] {
  const first = period + "-01";
  let last = monthEnd(first);
  if (last > c.today) last = c.today;
  if (first > last) return [];
  const punches = matchedPunches(c, first, addDays(last, 1));
  const leaves = approvedLeaves(c);
  const names = deviceNames(c);
  const rows: T.DailyAttendance[] = [];
  for (let d = first; d <= last; d = addDays(d, 1)) {
    rows.push(...processDay({ date: d, employeeIds: empIds, punches, leaves, policy: c.state.settings.attendance, nowHm: d === c.today ? c.nowHm : undefined, deviceNames: names }));
  }
  return rows;
}

export function expiringList(c: Ctx, days: number, onlyEmp?: number): T.ExpiringDoc[] {
  const out: T.ExpiringDoc[] = [];
  for (const e of c.active()) {
    if (onlyEmp != null && e.id !== onlyEmp) continue;
    for (const d of e.documents) {
      if (d.kind === "Work permit" || !isISODate(d.expiry)) continue;
      const left = daysTo(c.today, d.expiry);
      if (left <= days) out.push({ employeeId: e.id, kind: d.kind, number: d.number, expiry: d.expiry, daysLeft: left });
    }
  }
  return out.sort((a, b) => a.daysLeft - b.daysLeft || a.employeeId - b.employeeId);
}

export const APPROVAL_SLA: Record<T.Approval["kind"], number> = { Leave: 2, Loan: 3, Letter: 2, Expense: 3, Resignation: 5 };

export function approvals(c: Ctx): T.Approval[] {
  const out: T.Approval[] = [];
  const add = (kind: T.Approval["kind"], prefix: string, refId: number, employeeId: number, summary: string, when: string) =>
    out.push({ kind, id: prefix + refId, refId, employeeId, summary, when, slaDays: APPROVAL_SLA[kind] });
  const byId = <X extends { id: number }>(xs: X[]) => xs.slice().sort((a, b) => a.id - b.id);
  for (const l of byId(c.state.leaves.filter((x) => x.status === "Pending"))) add("Leave", "L", l.id, l.employeeId, `${l.type} leave · ${l.days} days · ${l.from} → ${l.to}`, l.from);
  for (const l of byId(c.state.loans.filter((x) => x.status === "Pending"))) add("Loan", "N", l.id, l.employeeId, `${l.type} · ${c.cur} ${fmtNum(l.amount)} over ${l.months} months`, l.date);
  for (const l of byId(c.state.letters.filter((x) => x.status === "Pending"))) add("Letter", "T", l.id, l.employeeId, `${l.kind} · to ${l.to}`, l.date);
  for (const x of byId(c.state.expenses.filter((v) => v.status === "Pending"))) add("Expense", "X", x.id, x.employeeId, `${x.vendor} · ${x.category} · ${c.cur} ${fmtNum(x.amount)}`, x.date);
  for (const ob of byId(c.state.offboarding.filter((v) => v.type === "Resignation" && v.status === "Notice period" && !v.acknowledged)))
    add("Resignation", "R", ob.id, ob.employeeId, "Resignation · last day " + ob.lastDay, ob.submitted);
  return out;
}

export function unmatched(c: Ctx): T.UnmatchedUser[] {
  const groups = new Map<string, T.UnmatchedUser>();
  for (const p of c.state.punches) {
    if (p.employeeId != null) continue;
    const k = p.deviceId + "|" + p.deviceUserId;
    let g = groups.get(k);
    if (!g) { g = { deviceId: p.deviceId, deviceUserId: p.deviceUserId, name: c.state.deviceUsers[k] ?? "", punches: 0, firstSeen: p.time, lastSeen: p.time }; groups.set(k, g); }
    g.punches++;
    if (p.time < g.firstSeen) g.firstSeen = p.time;
    if (p.time > g.lastSeen) g.lastSeen = p.time;
  }
  return [...groups.values()].sort((a, b) => (a.deviceId < b.deviceId ? -1 : a.deviceId > b.deviceId ? 1 : a.deviceUserId < b.deviceUserId ? -1 : a.deviceUserId > b.deviceUserId ? 1 : 0));
}

export function leaveBalanceOf(c: Ctx, e: T.Employee): T.LeaveBalance {
  const ent = annualEntitlement(e, c.today, c.country);
  let used = usedAnnualDemo(e);
  const yearStart = c.today.slice(0, 4) + "-01-01";
  for (const l of c.state.leaves) {
    if (l.employeeId === e.id && l.status === "Approved" && l.type === "Annual" && l.id > c.state.seedLeaveMax && l.from >= yearStart) used += l.days;
  }
  return { employeeId: e.id, entitlement: ent, used, remaining: Math.max(0, ent - used) };
}

/** Stores generated content (WPS file, letters) in the mock file store. */
export function putFile(c: Ctx, folder: string, name: string, contentType: string, data: Blob | string): T.FileRef {
  const blob = typeof data === "string" ? new Blob([data], { type: contentType }) : data;
  const key = `${c.tid}/${folder}/${c.today.slice(0, 4)}/${c.today.slice(5, 7)}/${uuid()}-${sanitizeName(name)}`;
  const u = (globalThis as { URL?: { createObjectURL?: (b: Blob) => string } }).URL;
  let url = "mock://" + key;
  if (u && typeof u.createObjectURL === "function") {
    try { url = u.createObjectURL(blob); } catch { /* jsdom: not implemented */ }
  }
  const ref: T.FileRef = { key, url, name, size: blob.size, contentType };
  c.state.files.set(key, { ref, blob, tenantId: c.tid });
  return ref;
}

export interface TenantContext {
  tenantName: string; cr: string; today: string; headcount: number; payrollGross: number; payrollNet: number; payrollPeriod: string;
  expiringDocs: number; saudizationPct: number; saudizationBand: string; pendingLeaves: number; expectedInvoice: number; nextPaymentRef: string;
  /** Currency code and Arabic word of the tenant's country (Go ai.TenantContext.Currency / CurrencyAr). */
  currency: string; currencyAr: string;
}

export function tenantContext(c: Ctx): TenantContext {
  const v = runView(c);
  const n = nitaqat(c.employeesSorted(), 0, 0);
  const sub = c.state.subscription;
  let expectedInvoice = 0;
  try { expectedInvoice = subAmount(c.state.planPrices, sub.plan, sub.cycle, sub.seats, sub.aiPack).total; } catch { /* unknown plan */ }
  const maxSeq = Math.max(1044, ...c.state.payments.map((p) => p.seq));
  const t = c.state.settings.tenant;
  return {
    tenantName: t.name, cr: t.cr, today: c.today, headcount: v.totals.headcount, payrollGross: v.totals.gross, payrollNet: v.totals.net, payrollPeriod: v.run.period,
    expiringDocs: expiringList(c, 90).length, saudizationPct: n.pct, saudizationBand: n.band,
    pendingLeaves: c.state.leaves.filter((l) => l.status === "Pending").length, expectedInvoice, nextPaymentRef: paymentRef(t.cr, maxSeq + 1),
    currency: countryOf(t.country).currency.code, currencyAr: countryOf(t.country).currency.word[1],
  };
}

export function parsePeriod(period: string): void {
  if (!/^(\d{4})-(0[1-9]|1[0-2])$/.test(period)) throw E.field("period", "must be YYYY-MM");
}
